import { type FC, useState } from 'react';
import {
    Box,
    Flex,
    ListItem,
    OrderedList,
    UnorderedList,
    useBreakpointValue,
    useDisclosure,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSubmitQuestion } from '../../../bff-api/turn-to/useSubmitQuestion';
import { useTurnTo } from '../../../contexts/useTurnTo';
import { Header, Text } from '../../../ui-components';
import { Field } from '../../field';
import { useSubmitQuestionFields } from '../../forms/useSubmitQuestionsFields';
import { TurnToSelect } from './Select';
import { SubmitQuestionModal } from './SubmitQuestionModal';
import type { CreateQuestionTypes, QuestionTypes } from '../../../bff-api/turn-to/model';
import type { QuestionSortOptions } from '../types';

export interface QuestionsHeaderProps {
    questionsList: QuestionTypes[];
    productId: string;
}
export const QuestionsHeader: FC<QuestionsHeaderProps> = ({ questionsList, productId }) => {
    const intl = useIntl();
    const [showTips, setShowTips] = useState<boolean>(false);

    const device = useBreakpointValue<'mobile' | 'desktop'>({
        base: 'mobile',
        'lg-sm': 'desktop',
    });
    const submitQuestion = useSubmitQuestion();
    const { sortQuestions, questionsSortOption, questionsNumber } = useTurnTo();
    const {
        isOpen: isOpenSubmitQuestion,
        onOpen: onOpenSubmitQuestion,
        onClose: onCloseSubmitQuestion,
    } = useDisclosure();

    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: { questionText: '', firstName: '', lastName: '', email: '', nickName: '' },
    });

    const sortOptions = [
        { value: 'mostRecent', label: 'Most recent' },
        { value: 'oldest', label: 'Oldest Question' },
        { value: 'mostCommon', label: 'Most common' },
        { value: 'mostAnswers', label: 'Most answers' },
        { value: 'fewestAnswers', label: 'Fewest Answers' },
    ];

    const onToggleTips = () => {
        setShowTips(!showTips);
    };

    const fields = useSubmitQuestionFields(form, onToggleTips);

    const userQuestion = form.watch('questionText');
    const onSubmit = async () => {
        await form.trigger();
        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }

        await onSubmitQuestion();
    };

    const onSubmitQuestion = async () => {
        const values = form.getValues();
        await submitQuestion.mutateAsync({
            text: userQuestion,
            user: {
                emailAddress: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                nickName: values.nickName,
            },
            catalogItems: [
                {
                    sku: productId,
                },
            ],
        } as unknown as CreateQuestionTypes);
        form.reset({});
        onCloseSubmitQuestion();
    };
    return (
        <Box
            paddingBottom={questionsNumber === 0 ? { base: 0 } : { base: 6, 'lg-sm': 8 }}
            borderColor="slt-pepper.400"
        >
            {device === 'desktop' && (
                <Header as="h3" margin="0 0 24px 0" variant="header3">
                    <FormattedMessage
                        id="turn-to.questions-and-answers"
                        defaultMessage="Questions & Answers"
                    />
                </Header>
            )}
            <Flex
                className={device === 'desktop' ? 'space-x-4' : 'space-y-4'}
                alignItems="flex-start"
                flexWrap={{ base: 'wrap', xl: 'nowrap' }}
            >
                <Box flexGrow={200} w={{ base: '100%', 'lg-sm': 'unset' }}>
                    <form onSubmit={form.handleSubmit(onOpenSubmitQuestion)}>
                        <Field {...fields.questionText} />
                    </form>
                </Box>
                <Flex
                    background="slt-grey.100"
                    className="space-x-6"
                    paddingX={4}
                    paddingY={{ base: 2, 'lg-sm': 0 }}
                    alignItems="center"
                    flexGrow={1}
                    justifyContent="space-between"
                >
                    <Text>
                        <FormattedMessage
                            id="turn-to.total-number-of-questions"
                            defaultMessage="{questionsNumber} Questions"
                            values={{
                                questionsNumber: questionsList ? questionsNumber : 0,
                            }}
                        />
                    </Text>
                    <Box width="186px">
                        <TurnToSelect
                            options={sortOptions}
                            id="questionSort"
                            name="questionSort"
                            onChange={(option) =>
                                sortQuestions(option.value as QuestionSortOptions)
                            }
                            value={sortOptions.find((opt) => opt.value === questionsSortOption)}
                            isDisabled={questionsNumber === 0}
                        />
                    </Box>
                </Flex>
            </Flex>
            {showTips && (
                <Flex marginTop={4} padding={5} className="space-x-4">
                    <Box flex="1">
                        <Text>
                            <FormattedMessage
                                id="turn-to.show-tips-col-1-header"
                                defaultMessage="Instant Answers"
                            />
                        </Text>
                        <OrderedList marginTop={3}>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-1-item-1"
                                        defaultMessage="Start typing and we'll see if it was already asked and answered."
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-1-item-2"
                                        defaultMessage="If there aren't already some matches, submit a new question."
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-1-item-3"
                                        defaultMessage="You'll get fast answers from customers who really own the item(s) and from our product experts. (About half the time you'll get an answer in under 2 hours!)"
                                    />
                                </Text>
                            </ListItem>
                        </OrderedList>
                    </Box>
                    <Box flex="1">
                        <Text>
                            <FormattedMessage
                                id="turn-to.show-tips-col-2-header"
                                defaultMessage="Good Topics To Ask About"
                            />
                        </Text>
                        <UnorderedList marginTop={3} styleType="none" marginLeft={0}>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-2-item-1"
                                        defaultMessage="Which items will best meet your needs"
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-2-item-2"
                                        defaultMessage="What customers who own an item think of it"
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-2-item-3"
                                        defaultMessage="How to use, fix, or take care of an item"
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-2-item-4"
                                        defaultMessage="Product information"
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-2-item-5"
                                        defaultMessage="General advice related to the types of products we sell"
                                    />
                                </Text>
                            </ListItem>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-2-item-6"
                                        defaultMessage="Our store policies"
                                    />
                                </Text>
                            </ListItem>
                        </UnorderedList>
                    </Box>
                    <Box flex="1">
                        <Text>
                            <FormattedMessage
                                id="turn-to.show-tips-col-1-header"
                                defaultMessage="Instant Answers"
                            />
                        </Text>
                        <UnorderedList marginTop={3} styleType="none" marginLeft={0}>
                            <ListItem>
                                <Text size="small">
                                    <FormattedMessage
                                        id="turn-to.show-tips.col-3-item-1"
                                        defaultMessage="For questions about an order you have placed, please contact customer support directly."
                                    />
                                </Text>
                            </ListItem>
                        </UnorderedList>
                    </Box>
                </Flex>
            )}
            <SubmitQuestionModal
                id="123"
                isOpen={isOpenSubmitQuestion}
                onClose={onCloseSubmitQuestion}
                fields={fields}
                onSubmit={onSubmit}
                onSkip={onSubmitQuestion}
                title={intl.formatMessage({
                    id: 'turn-to.question-modal.title',
                    defaultMessage: 'Have answers emailed to you',
                })}
                headerText={
                    <FormattedMessage
                        id="turn-to.question-modal.description"
                        defaultMessage="Your question has been submitted. Please check back here for answers, or enter your name and email address to have answers emailed to you."
                    />
                }
            />
        </Box>
    );
};
